<template>
  <Teleport to="body">
    <div v-if="modelValue" class="int-modal-overlay" @click.self="close">
      <div class="int-modal int-modal--xl api-explorer-modal" role="dialog" aria-modal="true" @click.stop>
        <div class="int-modal__accent" />
        <header class="int-modal__header">
          <div class="int-modal__icon-wrap">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          </div>
          <div class="int-modal__titles">
            <h3>{{ t('integracao.leadApi.explorerTitle') }}</h3>
            <p class="int-modal__subtitle">{{ t('integracao.leadApi.explorerDesc') }}</p>
          </div>
          <button type="button" class="int-modal__close" :aria-label="t('common.close')" @click="close">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div class="api-explorer">
          <aside class="api-explorer__sidebar">
            <div class="api-explorer__group">
              <p class="api-explorer__group-label">{{ t('integracao.leadApi.groupCreate') }}</p>
              <button
                v-for="ep in createEndpoints"
                :key="ep.id"
                type="button"
                :class="['api-explorer__nav-item', { 'api-explorer__nav-item--active': selectedId === ep.id }]"
                @click="selectEndpoint(ep.id)"
              >
                <span :class="['api-explorer__method', `api-explorer__method--${ep.method.toLowerCase()}`]">{{ ep.method }}</span>
                <span class="api-explorer__nav-title">{{ t(ep.titleKey) }}</span>
              </button>
            </div>
            <div class="api-explorer__group">
              <p class="api-explorer__group-label">{{ t('integracao.leadApi.groupCrm') }}</p>
              <button
                v-for="ep in crmEndpoints"
                :key="ep.id"
                type="button"
                :class="['api-explorer__nav-item', { 'api-explorer__nav-item--active': selectedId === ep.id }]"
                @click="selectEndpoint(ep.id)"
              >
                <span :class="['api-explorer__method', `api-explorer__method--${ep.method.toLowerCase()}`]">{{ ep.method }}</span>
                <span class="api-explorer__nav-title">{{ t(ep.titleKey) }}</span>
              </button>
            </div>
          </aside>

          <div v-if="selected" class="api-explorer__main">
            <div class="api-explorer__scroll">
              <div class="api-explorer__endpoint-bar">
                <span :class="['api-explorer__method', `api-explorer__method--${selected.method.toLowerCase()}`]">{{ selected.method }}</span>
                <code class="api-explorer__path">{{ displayRelativePath }}</code>
              </div>
              <div class="api-explorer__full-url">
                <span class="api-explorer__full-url-label">{{ t('integracao.leadApi.fullUrlLabel') }}</span>
                <code class="api-explorer__full-url-value">{{ displayFullUrl }}</code>
              </div>
              <p class="api-explorer__desc">{{ t(selected.descKey) }}</p>

              <div class="api-explorer__warn">{{ t('integracao.leadApi.realRequestWarning') }}</div>

              <div class="api-explorer__field">
                <label for="api-explorer-key">{{ t('integracao.leadApi.apiKeyLabel') }}</label>
                <input
                  id="api-explorer-key"
                  v-model="apiKey"
                  type="password"
                  autocomplete="off"
                  :placeholder="t('integracao.leadApi.apiKeyPlaceholder')"
                />
              </div>

              <div v-if="selected.queryParams?.length" class="api-explorer__params-grid">
                <div v-for="param in selected.queryParams" :key="param.key" class="api-explorer__field">
                  <label :for="`param-${param.key}`">{{ t(param.labelKey) }}</label>
                  <input
                    :id="`param-${param.key}`"
                    v-model="queryValues[param.key]"
                    :type="param.type === 'number' ? 'number' : 'text'"
                  />
                </div>
              </div>

              <div v-if="selected.bodyTemplate" class="api-explorer__field">
                <label for="api-explorer-body">Body (JSON)</label>
                <textarea id="api-explorer-body" v-model="bodyJson" spellcheck="false" />
                <p v-if="jsonError" class="api-explorer__json-error">{{ jsonError }}</p>
              </div>

              <div class="api-explorer__actions">
                <button
                  type="button"
                  class="api-explorer__test-btn"
                  :disabled="testing"
                  @click="executeTest"
                >
                  <svg v-if="testing" class="api-explorer__spinner" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {{ testing ? t('integracao.leadApi.testing') : t('integracao.leadApi.testButton') }}
                </button>
              </div>

              <div class="api-explorer__response">
                <div class="api-explorer__response-head">
                  <h4 class="api-explorer__response-title">{{ t('integracao.leadApi.responseTitle') }}</h4>
                  <span v-if="currentResponse?.status != null" :class="['api-explorer__status', statusClass(currentResponse.status)]">
                    {{ currentResponse.status }}
                  </span>
                  <span v-if="currentResponse?.durationMs != null" class="api-explorer__duration">
                    {{ currentResponse.durationMs }} ms
                  </span>
                </div>
                <pre v-if="currentResponse?.formatted" class="api-explorer__response-body">{{ currentResponse.formatted }}</pre>
                <p v-else class="api-explorer__response-empty">{{ t('integracao.leadApi.responseEmpty') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import publicApi from '@/services/publicApi'
import { getAbsoluteBackendUrl } from '@/config/app'
import {
  LEAD_API_EXPLORER_ENDPOINTS,
  buildEndpointUrl,
  getFullPath,
  defaultQueryValues,
  defaultBodyJson
} from '@/constants/leadApiExplorerEndpoints'
import '@/assets/styles/integration-modal.css'
import '@/assets/styles/lead-api-explorer.css'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  initialApiKey: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue'])

const { t } = useI18n()
const toast = useToast()

const selectedId = ref('createLead')
const apiKey = ref('')
const queryValues = reactive({})
const bodyJson = ref('')
const jsonError = ref('')
const testing = ref(false)
const responses = reactive({})

const createEndpoints = computed(() => LEAD_API_EXPLORER_ENDPOINTS.filter(e => e.group === 'create'))
const crmEndpoints = computed(() => LEAD_API_EXPLORER_ENDPOINTS.filter(e => e.group === 'crm'))

const selected = computed(() => LEAD_API_EXPLORER_ENDPOINTS.find(e => e.id === selectedId.value) || null)

const displayRelativePath = computed(() => {
  if (!selected.value) return ''
  return getFullPath(selected.value, queryValues)
})

const displayFullUrl = computed(() => {
  if (!selected.value) return ''
  return buildEndpointUrl(getAbsoluteBackendUrl(), selected.value.path, queryValues)
})

const currentResponse = computed(() => responses[selectedId.value] || null)

function close () {
  emit('update:modelValue', false)
}

function selectEndpoint (id) {
  selectedId.value = id
  loadEndpointState(id)
}

function loadEndpointState (id) {
  const ep = LEAD_API_EXPLORER_ENDPOINTS.find(e => e.id === id)
  if (!ep) return
  const defaults = defaultQueryValues(ep)
  Object.keys(queryValues).forEach(k => { delete queryValues[k] })
  Object.assign(queryValues, defaults)
  bodyJson.value = defaultBodyJson(ep)
  jsonError.value = ''
}

function statusClass (status) {
  if (status >= 200 && status < 300) return 'api-explorer__status--ok'
  if (status >= 400 && status < 500) return 'api-explorer__status--warn'
  return 'api-explorer__status--err'
}

function formatResponseData (data) {
  if (data == null) return ''
  if (typeof data === 'string') return data
  try {
    return JSON.stringify(data, null, 2)
  } catch {
    return String(data)
  }
}

function parseBody () {
  jsonError.value = ''
  if (!selected.value?.bodyTemplate) return undefined
  const raw = bodyJson.value?.trim()
  if (!raw) return {}
  try {
    return JSON.parse(raw)
  } catch {
    jsonError.value = t('integracao.leadApi.invalidJson')
    return null
  }
}

async function executeTest () {
  if (!selected.value) return
  if (!apiKey.value?.trim()) {
    toast.error(t('integracao.leadApi.apiKeyRequired'))
    return
  }

  const body = parseBody()
  if (body === null) return

  const url = getFullPath(selected.value, queryValues)
  const headers = {
    Authorization: `Bearer ${apiKey.value.trim()}`
  }

  testing.value = true
  const started = performance.now()

  try {
    const config = {
      method: selected.value.method,
      url,
      headers,
      validateStatus: () => true
    }
    if (body !== undefined) {
      config.data = body
    }
    const res = await publicApi.request(config)
    const durationMs = Math.round(performance.now() - started)
    responses[selectedId.value] = {
      status: res.status,
      data: res.data,
      formatted: formatResponseData(res.data),
      durationMs
    }
  } catch (e) {
    const durationMs = Math.round(performance.now() - started)
    const errData = e.response?.data ?? { error: e.message || 'Network error' }
    responses[selectedId.value] = {
      status: e.response?.status ?? 0,
      data: errData,
      formatted: formatResponseData(errData),
      durationMs
    }
  } finally {
    testing.value = false
  }
}

watch(() => props.modelValue, (open) => {
  if (open) {
    if (props.initialApiKey) {
      apiKey.value = props.initialApiKey
    }
    loadEndpointState(selectedId.value)
  }
})

watch(() => props.initialApiKey, (key) => {
  if (key && props.modelValue) {
    apiKey.value = key
  }
})

loadEndpointState(selectedId.value)
</script>

<style scoped>
.api-explorer__spinner {
  animation: api-explorer-spin 0.8s linear infinite;
}

@keyframes api-explorer-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.api-explorer-modal .int-modal__header {
  flex-shrink: 0;
}

.api-explorer-modal {
  max-width: min(1040px, 96vw);
}
</style>
